import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import React, { useEffect } from "react";
import { Phone } from "lucide-react";

// Layout Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import IntroVideoButton from "@/components/IntroVideoButton";
import AIChatWidget from "@/components/AIChatWidget";
import ScrollToTop from "@/components/ScrollToTop";
import ShadeCardSidebar from "@/components/ShadeCardSidebar";
import SEORedirectHandler from "@/components/SEORedirectHandler";
import AliasCatchAll from "@/components/AliasCatchAll";
import usePageContentOverrides from "@/hooks/usePageContentOverrides";
import usePageSections from "@/hooks/usePageSections";
import useImageAttributes from "@/hooks/useImageAttributes";
import usePageSEOOverride from "@/hooks/useSEOOverrides";

// Pages
import HomePage from "@/pages/HomePage";
import SolutionsPage from "@/pages/SolutionsPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import ManufacturingPage from "@/pages/ManufacturingPage";
import AboutPage from "@/pages/AboutPage";
import WhyChooseEzeefit from "@/pages/WhyChooseEzeefit";
import ProcessPage from "@/pages/ProcessPage";
import PartnersPage from "@/pages/PartnersPage";
import DistributorsPage from "@/pages/DistributorsPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import BlogPage from "@/pages/BlogPage";
import BlogDetail from "@/pages/BlogDetail";
import ContactPage from "@/pages/ContactPage";
import LocationsPage from "@/pages/LocationsPage";
import CityLandingPage from "@/pages/CityLandingPage";
import SEOCityPage from "@/pages/seo/SEOCityPage";
import PowerPage from "@/pages/seo/PowerPage";
import CategoryAuthorityPage from "@/pages/seo/CategoryAuthorityPage";
import ProductDetail from "@/pages/products/ProductDetail";
import BuyersGuidePage from "@/pages/BuyersGuidePage";
import CataloguePage from "@/pages/CataloguePage";
import ProductsPage from "@/pages/ProductsPage";
import MetroLandingPage from "@/pages/MetroLandingPage";
import OfficeLayoutGuidePage from "@/pages/OfficeLayoutGuidePage";
import FastOfficeInstallationPage from "@/pages/FastOfficeInstallationPage";
import SustainableOfficeFurniturePage from "@/pages/SustainableOfficeFurniturePage";

// Portal Pages
import PortalLogin from "@/pages/PortalLogin";
import ResetPassword from "@/pages/ResetPassword";
import ClientDashboard from "@/pages/ClientDashboard";
import StaffDashboard from "@/pages/StaffDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SEOEditorPortal from "@/pages/SEOEditorPortal";
import SEOMonitorPWA from "@/pages/SEOMonitorPWA";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundCancellationPolicy from "@/pages/RefundCancellationPolicy";

function AppShell() {
  const location = useLocation();
  const isPortalRoute = location.pathname.startsWith('/portal/admin')
    || location.pathname.startsWith('/seo-admin')
    || location.pathname.startsWith('/seo-monitor');

  // Apply admin-approved copy overrides on every PUBLIC page render.
  // Hook handles its own bailout when path is empty / portal route.
  usePageContentOverrides(isPortalRoute ? null : location.pathname);
  usePageSections(isPortalRoute ? null : location.pathname.replace(/\/+$/, "") || "/");

  // Apply Speicher-configured image alt + title overrides to every <img> on public pages.
  useImageAttributes();

  // Global SEO override applier — guarantees Speicher's meta title / description /
  // canonical / og-tags / H1 / JSON-LD schema edits apply on EVERY public page,
  // even ones whose page component doesn't call usePageSEOOverride internally
  // (e.g. Manufacturing, Process, Solutions, Blog, Case Studies etc.).
  // The hook's own cache prevents double-fetch when a page also calls it.
  usePageSEOOverride(isPortalRoute ? null : location.pathname, {});

  if (isPortalRoute) {
    return (
      <main className="flex-1">
        <Routes>
          <Route path="/portal/admin" element={<AdminDashboard />} />
          <Route path="/seo-admin" element={<SEOEditorPortal />} />
          <Route path="/seo-monitor" element={<SEOMonitorPWA />} />
        </Routes>
        <Toaster position="top-right" />
      </main>
    );
  }

  return (
    <>
      <SEORedirectHandler />
      <Navbar />
      <main className="flex-1">
        <PublicRoutes />
      </main>
      <Footer />
      <ShadeCardSidebar />
      <IntroVideoButton />
      <AIChatWidget />
      <PublicFloatingCTAs />
      <Toaster position="top-right" />
    </>
  );
}

function App() {

  useEffect(() => {
    // --- Minimal Content Protection (Google Ads compliant) ---
    // Bypassed for:
    // 1. Admin / SEO-admin pages
    // 2. Logged-in SEO editor sessions (they need to inspect & copy for audits)
    // 3. URL param ?seo-audit=1 (one-off explicit override)
    const isAdminPage = () => {
      const p = window.location.pathname;
      return p.startsWith('/portal/admin') || p.startsWith('/seo-admin');
    };
    const isSeoEditorLoggedIn = () => !!localStorage.getItem('seo_editor_token');
    const hasAuditFlag = () => {
      try {
        return new URLSearchParams(window.location.search).get('seo-audit') === '1'
          || localStorage.getItem('seo_audit_mode') === '1';
      } catch (e) { return false; }
    };
    // Persist ?seo-audit=1 across nav until explicitly cleared via ?seo-audit=0
    try {
      const param = new URLSearchParams(window.location.search).get('seo-audit');
      if (param === '1') localStorage.setItem('seo_audit_mode', '1');
      if (param === '0') localStorage.removeItem('seo_audit_mode');
    } catch (e) { /* noop */ }

    const bypassProtection = () => isAdminPage() || isSeoEditorLoggedIn() || hasAuditFlag();

    // Block right-click context menu
    const handleContextMenu = (e) => {
      if (!bypassProtection()) e.preventDefault();
    };

    // Block image drag
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG' && !bypassProtection()) e.preventDefault();
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col overflow-x-hidden">
        <AppShell />
      </div>
    </BrowserRouter>
  );
}

function PublicRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/solutions/:industry" element={<SolutionsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/why-choose-ezeefit" element={<WhyChooseEzeefit />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
      <Route path="/manufacturing" element={<ManufacturingPage />} />
      <Route path="/process" element={<ProcessPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/distributors" element={<DistributorsPage />} />
      <Route path="/testimonials" element={<TestimonialsPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogDetail />} />
      <Route path="/locations" element={<LocationsPage />} />
      <Route path="/locations/:city" element={<CityLandingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/buyers-guide" element={<BuyersGuidePage />} />
      <Route path="/catalogue" element={<CataloguePage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/office-layout-seating-plan-guide" element={<OfficeLayoutGuidePage />} />
      <Route path="/fast-office-installation" element={<FastOfficeInstallationPage />} />
      <Route path="/sustainable-office-furniture-india" element={<SustainableOfficeFurniturePage />} />
      <Route path="/products/:slug" element={<ProductDetail />} />
      <Route path="/office-furniture-mumbai" element={<PowerPage />} />
      <Route path="/modular-office-workstations-mumbai" element={<CategoryAuthorityPage />} />
      <Route path="/office-partitions-and-cubicles" element={<CategoryAuthorityPage />} />
      <Route path="/office-chairs-mumbai" element={<CategoryAuthorityPage />} />
      <Route path="/corporate-office-furniture-solutions" element={<CategoryAuthorityPage />} />
      <Route path="/office-furniture-manufacturer-mumbai" element={<CategoryAuthorityPage />} />
      {/* Pan-India / metro-cities landing page (Google Ads campaign target — non-Mumbai) */}
      <Route path="/modular-office-furniture-manufacturer" element={<MetroLandingPage />} />
      <Route path="/office-workspace-solutions-mumbai" element={<SEOCityPage />} />
      <Route path="/modular-office-workstations-navi-mumbai" element={<SEOCityPage />} />
      <Route path="/corporate-office-furniture-thane" element={<SEOCityPage />} />
      <Route path="/office-furniture-solutions-pune" element={<SEOCityPage />} />
      <Route path="/corporate-office-furniture-bangalore" element={<SEOCityPage />} />
      <Route path="/office-furniture-solutions-delhi-ncr" element={<SEOCityPage />} />
      <Route path="/modular-office-furniture-hyderabad" element={<SEOCityPage />} />
      <Route path="/office-furniture-chennai" element={<SEOCityPage />} />
      <Route path="/office-furniture-ahmedabad" element={<SEOCityPage />} />
      {/* NEW micro-market & city pages */}
      <Route path="/office-furniture-bkc-mumbai" element={<SEOCityPage />} />
      <Route path="/office-furniture-andheri-powai" element={<SEOCityPage />} />
      <Route path="/office-furniture-gurugram" element={<SEOCityPage />} />
      {/* NEW solution-keyword pages targeting Mumbai search intent */}
      <Route path="/modular-cabin-partitions-mumbai" element={<SEOCityPage />} />
      <Route path="/acoustic-phone-booths-mumbai" element={<SEOCityPage />} />
      <Route path="/acoustic-pods-booths-india" element={<SEOCityPage />} />
      <Route path="/modular-office-furniture-maharashtra" element={<SEOCityPage />} />
      <Route path="/bpo-call-center-furniture-mumbai" element={<SEOCityPage />} />
      {/* 301 redirects — alternate slugs the SEO agency may target → canonical existing pages */}
      <Route path="/office-furniture-pune" element={<Navigate to="/office-furniture-solutions-pune" replace />} />
      <Route path="/office-furniture-bengaluru" element={<Navigate to="/corporate-office-furniture-bangalore" replace />} />
      <Route path="/office-furniture-hyderabad" element={<Navigate to="/modular-office-furniture-hyderabad" replace />} />
      <Route path="/office-furniture-navi-mumbai" element={<Navigate to="/modular-office-workstations-navi-mumbai" replace />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund-cancellation" element={<RefundCancellationPolicy />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/reset-password" element={<ResetPassword />} />
      <Route path="/portal/client" element={<ClientDashboard />} />
      <Route path="/portal/staff" element={<StaffDashboard />} />
      {/* Catch-all: any URL that doesn't match a real route falls here. Component checks
          the SEO redirect table — if there's a rule pointing TO this URL from a real page,
          serve the source page's content under this URL (SEO slug-rename support). URL
          stays at the alias so canonical + meta + body-content overrides configured for the
          alias URL in the SEO portal all apply correctly. */}
      <Route path="*" element={<AliasCatchAll componentMap={SOURCE_COMPONENT_MAP} />} />
    </Routes>
  );
}

// Map of source-path → rendered element. Used by AliasCatchAll so when a redirect points
// TO an unknown URL FROM one of these paths, we render THIS element in-place under the alias
// URL (React Router never navigates away, so URL bar and all override hooks see the alias URL).
const SOURCE_COMPONENT_MAP = {
  "/": <HomePage />,
  "/about": <AboutPage />,
  "/products": <ProductsPage />,
  "/solutions": <SolutionsPage />,
  "/case-studies": <CaseStudiesPage />,
  "/contact": <ContactPage />,
  "/blog": <BlogPage />,
  "/manufacturing": <ManufacturingPage />,
  "/process": <ProcessPage />,
  "/why-choose-ezeefit": <WhyChooseEzeefit />,
  "/testimonials": <TestimonialsPage />,
  "/locations": <LocationsPage />,
  "/catalogue": <CataloguePage />,
  "/office-layout-seating-plan-guide": <OfficeLayoutGuidePage />,
  "/fast-office-installation": <FastOfficeInstallationPage />,
  "/sustainable-office-furniture-india": <SustainableOfficeFurniturePage />,
  "/office-furniture-mumbai": <PowerPage />,
  "/modular-office-workstations-mumbai": <CategoryAuthorityPage />,
  "/office-partitions-and-cubicles": <CategoryAuthorityPage />,
  "/office-chairs-mumbai": <CategoryAuthorityPage />,
  "/corporate-office-furniture-solutions": <CategoryAuthorityPage />,
  "/office-furniture-manufacturer-mumbai": <CategoryAuthorityPage />,
  "/modular-office-furniture-manufacturer": <MetroLandingPage />,
  "/office-workspace-solutions-mumbai": <SEOCityPage />,
  "/modular-office-workstations-navi-mumbai": <SEOCityPage />,
  "/corporate-office-furniture-thane": <SEOCityPage />,
  "/office-furniture-solutions-pune": <SEOCityPage />,
  "/corporate-office-furniture-bangalore": <SEOCityPage />,
  "/office-furniture-solutions-delhi-ncr": <SEOCityPage />,
  "/modular-office-furniture-hyderabad": <SEOCityPage />,
  "/office-furniture-chennai": <SEOCityPage />,
  "/office-furniture-ahmedabad": <SEOCityPage />,
  "/office-furniture-bkc-mumbai": <SEOCityPage />,
  "/office-furniture-andheri-powai": <SEOCityPage />,
  "/office-furniture-gurugram": <SEOCityPage />,
  "/modular-cabin-partitions-mumbai": <SEOCityPage />,
  "/acoustic-phone-booths-mumbai": <SEOCityPage />,
  "/acoustic-pods-booths-india": <SEOCityPage />,
  "/modular-office-furniture-maharashtra": <SEOCityPage />,
  "/bpo-call-center-furniture-mumbai": <SEOCityPage />,
};

function PublicFloatingCTAs() {
  return (
    <>
      {/* Talk to an Expert — VERTICAL side strip on the left edge (desktop + mobile, mirrors mobile design) */}
      <a
        href="tel:+919820596811"
        className="hidden md:flex fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-[#0F172A] text-white py-3 px-1.5 rounded-r-md shadow-lg flex-col items-center gap-2 hover:bg-[#1E293B] transition-colors"
        data-testid="talk-to-expert-btn"
        aria-label="Talk to an Expert — Call 9820596811"
      >
        <span className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>Talk to Expert</span>
        <Phone className="w-3.5 h-3.5 shrink-0" />
        <span className="text-[11px] phone-flash-dark whitespace-nowrap leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>9820596811</span>
        <span className="text-[10px] text-[#94A3B8] whitespace-nowrap leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>9820042100</span>
      </a>
      {/* Mobile keeps existing compact side strip */}
      <a href="tel:+919820596811" className="md:hidden fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-[#0F172A] text-white py-2.5 px-1 rounded-r-md shadow-lg flex flex-col items-center gap-1.5 active:bg-[#1E293B] transition-colors" data-testid="talk-to-expert-mobile-btn" aria-label="Talk to an Expert — Call 9820596811">
        <span className="text-[9px] font-bold uppercase tracking-wider whitespace-nowrap leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>Talk to Expert</span>
        <Phone className="w-3 h-3 shrink-0" />
        <span className="text-[10px] phone-flash-dark whitespace-nowrap leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>9820596811</span>
        <span className="text-[9px] text-[#94A3B8] whitespace-nowrap leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>9820042100</span>
      </a>
    </>
  );
}


export default App;
